import { useQuery } from '@tanstack/react-query';
import { Href, useRouter } from 'expo-router';
import { memo } from 'react';

import { MessageCard } from '@/components/inbox/MessageCard';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { DraftV2 } from '@/services/getDraftV2';
import { getUserPublicV2QueryOptions } from '@/services/getUserPublicV2';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

/**
 * Draft card component that displays draft inquiry content.
 *
 * The expert comes from their public profile. The private user endpoint answers only for the
 * caller themselves, so a card that asked it for the expert showed every addressed draft as
 * unassigned.
 * Wrapped with memo to prevent unnecessary re-renders when parent list updates.
 */
export const DraftCard = memo(function DraftCard({ content, updatedAt, uuid }: DraftV2) {
  const draftWithExpertIds = content as typeof content & { expertIds?: number[] | null };

  const expertIds = draftWithExpertIds.expertIds?.filter((id): id is number => id !== null) ?? [];

  const hasExpertId = content.expertId !== null && content.expertId !== undefined;

  /**
   * The expert the card names: the one a Directory Inquiry is addressed to, else the first expert
   * chosen on a General Inquiry. A Directory Inquiry's `expertIds` are its additional experts, so
   * they never outrank `expertId`.
   */
  const expertIdToUse = (() => {
    if (hasExpertId) {
      return content.expertId;
    }

    if (expertIds.length > 0) {
      return expertIds[0];
    }

    return null;
  })();

  const { data: expert, isLoading } = useQuery(
    getUserPublicV2QueryOptions(expertIdToUse ?? undefined)
  );

  const { data: cancerTypeData } = useQuery(getCancerTypesV2QueryOptions());

  const { navigate } = useRouter();

  const { name, nameWithoutDoctor } = expert
    ? getProfileNameAndInitials(expert)
    : {
        name: undefined,
        nameWithoutDoctor: undefined,
      };

  /**
   * Routes to appropriate inquiry form based on draft content.
   * If `expertId` exists → Directory Inquiry (`/directory/inquiry/[id]`), otherwise → Inquiry (`/inquiry`).
   * Routing based solely on `expertId` presence (handles both fixed API and legacy drafts).
   */
  const handlePress = () => {
    if (hasExpertId && content.expertId !== null) {
      navigate(`/directory/inquiry/${content.expertId}?draftId=${uuid}` as Href);

      return;
    }

    navigate(`/inquiry?draftId=${uuid}` as Href);
  };

  const hasNoExpert = expertIdToUse === null;

  return (
    <MessageCard
      id={uuid}
      lastRespondedAt={new Date(updatedAt)}
      name={hasNoExpert ? ' ' : name || ' '}
      nameWithoutDoctor={hasNoExpert ? ' ' : nameWithoutDoctor || ' '}
      avatarUrl={expert ? getAvatarUrl(expert.avatar) : undefined}
      message={content.message || '--'}
      subject={
        content.cancerTypeId
          ? (cancerTypeData?.cancerTypeLookup?.get(content.cancerTypeId)?.shortLabel ?? '')
          : ''
      }
      onPress={handlePress}
      isUserLoaded={!expertIdToUse || !isLoading}
      inquiryStatus="draft"
      currentTab="drafts"
    />
  );
});
